"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeModelCollectionReader = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const __1 = require("..");
const id_1 = require("../../../id");
const fetchable_1 = require("../../../rest/fetchable");
const form_1 = require("../../form");
const collection_data_1 = require("../collection.data");
const label_drawable_1 = require("../drawable/label.drawable");
const line_drawable_1 = require("../drawable/line.drawable");
const marker_drawable_1 = require("../drawable/marker.drawable");
const image_field_1 = require("../fields/image.field");
const number_field_1 = require("../fields/number.field");
const true_size_field_1 = require("../fields/true.size.field");
const ReadUtil = tslib_1.__importStar(require("./collection.model.util"));
class IkeModelCollectionReader {
    constructor(collection, schema, loader) {
        this.log = null;
        this.json = collection;
        this.schema = schema;
        this.loader = loader;
        this.collection = new collection_data_1.CollectionModel(schema, loader);
    }
    read() {
        const collectionJson = this.json;
        if (collectionJson.data != null && collectionJson.data.data != null) {
            this.collection.state.instanceId = collectionJson.data.data['_id'];
        }
        if (this.collection.instanceId == null) {
            this.collection.state.instanceId = collectionJson.objectId;
        }
        const state = this.collection.state;
        state.id = collectionJson.objectId;
        state.uuid = collectionJson.uuid;
        state.createdAt = new Date(collectionJson.createdAt);
        state.updatedAt = new Date(collectionJson.updatedAt);
        state.collectedBy = collectionJson.collectedBy;
        state.collectedWith = collectionJson.collectedWith;
        state.assignedTo = collectionJson.assignedTo;
        state.updatedBy = collectionJson.updatedBy;
        state.revision = collectionJson.revision;
        state.jobId = collectionJson.jobuuid;
        if (collectionJson.collectedAt != null) {
            state.collectedAt = core_api_1.ParseJSON.fromDate(collectionJson.collectedAt);
        }
        this.readMeasurements();
        this.readBrightnessCorrections();
        this.readCropParameters();
        this.readImageVisibilities();
        for (const field of this.schema.fields) {
            this.readField(this.collection, field, collectionJson.data.data);
        }
        return this.collection;
    }
    readField(target, schema, source) {
        const value = source[schema.id];
        // Check to make sure the value actually has something interesting in it.
        if (value == null) {
            return;
        }
        // Process sub forms
        if (form_1.FormModelFieldSubForm.isSubForm(schema)) {
            return this.loadSubForm(target, schema, value);
        }
        // Text based fields can just be set
        if (schema.type === core_api_1.IkeFieldType.TEXT) {
            const f = target.getField(schema.id);
            f.Mutator.manual(value);
            return;
        }
        if (schema.type === core_api_1.IkeFieldType.FILE_TOOL) {
            return this.loadFileTool(target, schema, value);
        }
        if (schema.type === core_api_1.IkeFieldType.DATE) {
            const f = target.getField(schema.id);
            f.Mutator.manual(value);
            return;
        }
        if (schema.type === core_api_1.IkeFieldType.YES_NO) {
            const f = target.getField(schema.id);
            f.Mutator.manual(value);
            return;
        }
        if (schema.type === core_api_1.IkeFieldType.SELECT_LIST) {
            const f = target.getField(schema.id);
            f.Mutator.manual(value);
            return;
        }
        if (schema.type === core_api_1.IkeFieldType.NUMBER) {
            return this.loadNumber(target, schema, value);
        }
        // Load in CaptureFile information
        if (schema.type === core_api_1.IkeFieldType.TRUE_SIZE) {
            return this.loadCaptures(target, schema, value);
        }
        if (schema.type === core_api_1.IkeFieldType.IMAGE) {
            return this.loadCaptures(target, schema, value);
        }
        if (schema.type === core_api_1.IkeFieldType.VECTOR) {
            return this.loadVector(target, schema, value);
        }
        if (schema.type === core_api_1.IkeFieldType.ANCHOR) {
            return this.loadAnchor(target, schema, value);
        }
        if (schema.type === core_api_1.IkeFieldType.LOCATION) {
            return this.loadLocation(target, schema, value);
        }
        if (schema.type === core_api_1.IkeFieldType.NESTED_LIST) {
            const nlField = target.getField(schema.id);
            nlField.Mutator.manual(value[0]);
            return;
        }
        if (schema.type === core_api_1.IkeFieldType.HEIGHT) {
            return this.loadHeight(target, schema, value);
        }
        if (schema.type === core_api_1.IkeFieldType.VERTICAL_SEPARATION) {
            return this.loadVerticalSeparation(target, schema, value);
        }
        throw new Error(`Unable to find field Serializer for ${schema.type}`);
    }
    loadSubForm(target, schema, source) {
        const instances = source;
        const field = target.getField(schema.id);
        if (instances.length === 0) {
            field.Mutator.clear();
        }
        for (const data of instances) {
            const instanceId = data._id || id_1.IkeId.generate();
            field.Mutator.create(instanceId);
            const instance = field.getInstance(instanceId);
            for (const f of schema.fields) {
                this.readField(instance, f, data);
            }
        }
    }
    loadCaptures(target, schema, source) {
        const field = target.getField(schema.id);
        if (source.length === 0) {
            if (true_size_field_1.CollectionModelFieldTrueSize.isTrueSizeField(field)) {
                field.Mutator.clear();
            }
            else if (image_field_1.CollectionModelFieldImage.isImageField(field)) {
                field.Mutator.clear();
            }
            return;
        }
        for (const ts of source) {
            if (true_size_field_1.CollectionModelFieldTrueSize.isTrueSizeField(field)) {
                field.Mutator.add(ts.capture.objectId);
            }
            else if (image_field_1.CollectionModelFieldImage.isImageField(field)) {
                field.Mutator.add(ts.capture.objectId);
            }
        }
    }
    loadLocation(target, schema, source) {
        const field = target.getField(schema.id);
        const isLocationData = (x) => x['provider'] == null;
        if (isLocationData(source)) {
            // We are unsure if this is actually a GPS reading or a manual reading.
            // In theory manual readings will not have accuracy
            if (source.accuracy != null) {
                field.Mutator.gps(source, null);
                return;
            }
            field.Mutator.manual(source);
            return;
        }
        if (source.provider === 'edited') {
            field.Mutator.edited(source.value);
            return;
        }
        if (source.provider === 'gps') {
            field.Mutator.gps(source.value, source);
            return;
        }
        if (source.provider === 'manual') {
            field.Mutator.manual(source.value);
            return;
        }
        field.Mutator.capture(source.capture.objectId || source.capture.uuid);
    }
    loadNumber(target, schema, source) {
        const field = target.getField(schema.id);
        if (typeof source === 'number') {
            field.Mutator.manual(source);
            return;
        }
        if (typeof source === 'string') {
            field.Mutator.manual(parseFloat(source));
            return;
        }
        if (source.provider === 'manual') {
            field.Mutator.manual(source.value);
            return;
        }
        if (source.provider === 'bearing' || source.provider === 'backbearing') {
            if (source.capture == null) {
                // Some bad data doesn't have a "capture". Revert to manual.
                field.Mutator.manual(source.value);
                return;
            }
            if (source.provider === number_field_1.CollectionModelFieldNumber.PROVIDER_BEARING) {
                field.Mutator.bearing(source.capture.objectId || source.capture.uuid);
                return;
            }
            if (source.provider === number_field_1.CollectionModelFieldNumber.PROVIDER_BACK_BEARING) {
                field.Mutator.backBearing(source.capture.objectId || source.capture.uuid);
                return;
            }
        }
        if (source.provider === 'sag') {
            const points = source.points.map((p) => p.objectId || p.uuid);
            field.Mutator.sag(source.start.objectId || source.start.uuid, source.end.objectId || source.end.uuid, points);
            return;
        }
        throw new Error(`Unable to find field Serializer for ${schema.type} with provider ${source['provider']}`);
    }
    loadVector(target, schema, source) {
        const field = target.getField(schema.id);
        if (ReadUtil.isVectorPointToPointProvider(source)) {
            const cc = source.capture.points.map((ts) => ts.capture.objectId);
            field.Mutator.p2p(cc);
            return;
        }
        if (ReadUtil.isVectorCollectionProvider(source)) {
            field.Mutator.collection(source.collectionId);
            return;
        }
        if (ReadUtil.isVectorProviderManual(source)) {
            field.Mutator.manual(source.value);
            return;
        }
        return;
    }
    loadAnchor(target, schema, source) {
        const field = target.getField(schema.id);
        if (ReadUtil.isAnchorLocationGPS(source)) {
            field.Mutator.gps(source.value);
            if (source.antennaHeight != null) {
                field.Mutator.setAntennaHeight(source.antennaHeight);
            }
            return;
        }
        if (ReadUtil.isAnchorP2P(source)) {
            const typedSource = source;
            const cc = typedSource.capture.points.map((ts) => ts.capture.objectId);
            field.Mutator.p2p(cc, typedSource.value.placement);
            return;
        }
        if (ReadUtil.isAnchorManual(source)) {
            field.Mutator.manualAnchor(source.value);
            return;
        }
        return;
    }
    loadHeight(target, schema, source) {
        const field = target.getField(schema.id);
        if (ReadUtil.isHeightProviderManual(source)) {
            field.Mutator.manual(source.value);
            return;
        }
        if (ReadUtil.isHeightSpanHeightProvider(source)) {
            const cc = source.capture.points.map((ts) => ts.capture.objectId);
            field.Mutator.spanHeight(cc);
            return;
        }
        if (ReadUtil.isHeightPhotomeasureProvider(source) || ReadUtil.isHeightPhotomeasureManualProvider(source)) {
            for (const captureId of Array.from(this.collection.state.drawableStates.keys())) {
                const drawableState = this.collection.getDrawableState(captureId);
                if (!drawableState.hasDrawable(source.drawable)) {
                    continue;
                }
                const drawable = drawableState.getDrawable(source.drawable);
                if (drawable == null) {
                    field.Mutator.manual(null); // TODO should we error here?
                    return;
                }
                field.Mutator.drawable(drawable);
                return;
            }
            // Unable to find drawable
            field.Mutator.manual(source.value); // TODO should we error here?
            return;
        }
        throw new Error(`Field ${schema.id}: Unable to parse Provider`);
    }
    loadVerticalSeparation(target, schema, source) {
        var _a, _b, _c;
        const field = target.getField(schema.id);
        if (!source.drawableTop || !source.drawableBottom) {
            field.Mutator.manual(source.value); // TODO should we error here?
            return;
        }
        for (const captureId of Array.from(this.collection.state.drawableStates.keys())) {
            const drawableState = this.collection.getDrawableState(captureId);
            // if one of the pairs is missing, continue
            if (!drawableState.hasDrawable(source.drawableTop) || !drawableState.hasDrawable(source.drawableBottom)) {
                continue;
            }
            const drawableTop = drawableState.getDrawable(source.drawableTop);
            if (drawableTop == null) {
                (_a = this.log) === null || _a === void 0 ? void 0 : _a.error({ source, captureId, schema, drawableState }, "Expected to find drawableTop in drawableState but it wasn't found, setting field to null");
                field.Mutator.manual(null);
                return;
            }
            const drawableBottom = drawableState.getDrawable(source.drawableBottom);
            try {
                // If a vertical separation drawable was not persisted, create one.
                // We do this because vertical separation drawables are a more recent addition
                // and not all collections have them in their persisted state.
                const drawableVertSep = source.drawable
                    ? drawableState.getDrawable(source.drawable)
                    : __1.CollectionModelDrawableVerticalSeparation.createWithLabel(drawableState, drawableTop.id, drawableBottom.id, field.id);
                field.Mutator.setDrawables(drawableTop, drawableBottom, drawableVertSep);
                // Link marker labels after the state is set so they don't call field.Mutator.drawable().
                // This prevents an issue where the captureJSON is not loaded yet at load time.
                drawableTop.label.linkVerticalSeparation(field);
                drawableBottom.label.linkVerticalSeparation(field);
                (_b = this.log) === null || _b === void 0 ? void 0 : _b.debug({ fieldId: field.id }, 'Loaded Vertical Separation field');
            }
            catch (error) {
                if (error instanceof fetchable_1.IkeFetchableRaceError) {
                    (_c = this.log) === null || _c === void 0 ? void 0 : _c.warn({ error, source }, 'Caught IkeFetchableRaceError, skipping Vertical Separation Field');
                    return;
                }
                throw error;
            }
            return;
        }
        // Unable to find drawable
        field.Mutator.manual(source.value); // TODO should we error here?
    }
    loadFileTool(target, schema, source) {
        const field = target.getField(schema.id);
        field.Mutator.manual({
            filename: source.filename,
            attachmentId: source.attachmentId,
            size: source.size,
            metadata: source.metadata,
        });
    }
    readMeasurements() {
        const collectionJSON = this.json;
        if (collectionJSON.measurements == null) {
            return;
        }
        const measurements = Object.keys(collectionJSON.measurements);
        if (measurements.length === 0) {
            return;
        }
        for (const measurementId of measurements) {
            const measurement = collectionJSON.measurements[measurementId];
            if (measurement == null) {
                continue;
            }
            if (measurement.state.length === 0) {
                continue;
            }
            if (measurement.captureId == null) {
                throw new Error('Unable to read measurement, missing captureId ' + measurementId);
            }
            const drawableState = this.collection.getDrawableState(measurement.captureId);
            for (const drawable of measurement.state) {
                if (drawableState.drawables.has(drawable.id)) {
                    continue;
                }
                if (ReadUtil.isMarkerDrawable(drawable)) {
                    this.createMarkerDrawable(drawableState, drawable);
                }
                else if (ReadUtil.isLineDrawable(drawable)) {
                    this.createLineDrawable(drawableState, drawable);
                }
                else if (ReadUtil.isMaskDrawable(drawable)) {
                    this.createMaskDrawable(drawableState, drawable);
                }
                else if (ReadUtil.isVerticalSeparationDrawable(drawable)) {
                    this.createVerticalSeparationDrawable(drawableState, drawable);
                }
                else {
                    throw new Error('Unable to read drawable, unknown type ' + drawable.tag);
                }
            }
        }
    }
    createMarkerDrawable(drawableState, obj) {
        var _a, _b;
        if (!obj.label) {
            throw new Error(`Marker drawable requires a label state but one was not provided`);
        }
        this.createLabelDrawable(drawableState, obj.label, obj.id);
        switch (obj.tag) {
            case 'marker':
                // NOTE: Currently Base Offset markers can be serialized with "tag" set to "marker"
                // but they are not Markers -- they are Marker Offsets -- so we check "tag" and "type".
                switch (obj.type) {
                    case core_api_1.MarkerType.NONE:
                        return new marker_drawable_1.CollectionModelDrawableMarker(drawableState, obj.id, obj.y, (_a = obj.label) === null || _a === void 0 ? void 0 : _a.id, obj.type, obj.insight, obj.pair);
                    case core_api_1.MarkerType.BASE:
                        return new marker_drawable_1.CollectionModelDrawableMarker(drawableState, obj.id, obj.y, (_b = obj.label) === null || _b === void 0 ? void 0 : _b.id, obj.type, obj.insight, obj.pair, obj.offset);
                    case core_api_1.MarkerType.BASE_OFFSET:
                        return new __1.CollectionModelDrawableMarkerOffset(drawableState, obj.id, obj.label.id);
                    default:
                        throw new Error(`Unrecognized marker type: ${obj['type']}`);
                }
            case 'offset':
                return new __1.CollectionModelDrawableMarkerOffset(drawableState, obj.id, obj.label.id);
            default:
                throw new Error(`Unrecognized marker tag: ${obj.tag}`);
        }
    }
    createLabelDrawable(drawableState, obj, parentId) {
        return new label_drawable_1.CollectionModelDrawableLabel(drawableState, obj.id, obj.x, obj.y, parentId, typeof obj.text === 'string' ? [obj.text] : obj.text, undefined);
    }
    createLineDrawable(drawableState, obj) {
        this.createLabelDrawable(drawableState, obj.label, obj.id);
        const start = new __1.CollectionModelDrawableLinePoint(drawableState, `${obj.id}:start`, obj.start.x, obj.start.y, obj.id);
        const end = new __1.CollectionModelDrawableLinePoint(drawableState, `${obj.id}:end`, obj.end.x, obj.end.y, obj.id);
        return new line_drawable_1.CollectionModelDrawableLine(drawableState, obj.id, obj.label.id, start.id, end.id);
    }
    createMaskDrawable(drawableState, obj) {
        if (!obj.pukeko_data) {
            throw new Error(`Failed to deserialize Mask Drawable "${obj.id}": Pukeko data is missing`);
        }
        return new __1.CollectionModelDrawableMask(drawableState, obj.id, obj.pukeko_data);
    }
    createVerticalSeparationDrawable(drawableState, obj) {
        // Detect malformed data (primarily during development)
        if (!obj.topMarkerId || !obj.bottomMarkerId || !obj.fieldId || !obj.label || !obj.id) {
            throw new Error(`Malformed vertical separation drawable state encountered during JSON deserialization (collection ${this.collection.id}) (capture ${drawableState.captureId}): ${JSON.stringify(obj)}`);
        }
        const label = this.createLabelDrawable(drawableState, obj.label, obj.id);
        return new __1.CollectionModelDrawableVerticalSeparation(drawableState, obj.topMarkerId, obj.bottomMarkerId, obj.fieldId, label, obj.id);
    }
    readBrightnessCorrections() {
        const collectionJSON = this.json;
        if (collectionJSON.brightnessCorrections == null) {
            return;
        }
        const brightnessCorrections = Object.keys(collectionJSON.brightnessCorrections);
        if (brightnessCorrections.length === 0) {
            return;
        }
        for (const brightnessCorrectionId of brightnessCorrections) {
            const brightnessCorrection = collectionJSON.brightnessCorrections[brightnessCorrectionId];
            if (brightnessCorrection == null) {
                continue;
            }
            if (brightnessCorrection.state.length === 0) {
                continue;
            }
            if (brightnessCorrection.captureId == null) {
                throw new Error('Unable to read brightness correction, missing captureId ' + brightnessCorrectionId);
            }
            const brightnessCorrectionState = this.collection.getBrightnessCorrectionState(brightnessCorrection.captureId);
            for (const drawableBrightnessCorrection of brightnessCorrection.state) {
                if (brightnessCorrectionState.brightnessCorrections[drawableBrightnessCorrection.id]) {
                    continue;
                }
                if (ReadUtil.isGammaBrightnessCorrection(drawableBrightnessCorrection)) {
                    this.createGammaBrightnessCorrection(brightnessCorrectionState, drawableBrightnessCorrection);
                }
                else {
                    continue; // TODO should we error here?
                }
            }
        }
    }
    readImageVisibilities() {
        const collectionJSON = this.json;
        if (collectionJSON.imageVisibilities == null) {
            return;
        }
        const imageVisibilities = Object.keys(collectionJSON.imageVisibilities);
        if (imageVisibilities.length === 0) {
            return;
        }
        for (const imageVisibilityId of imageVisibilities) {
            const imageVisibility = collectionJSON.imageVisibilities[imageVisibilityId];
            if (imageVisibility == null || !imageVisibility.state) {
                continue;
            }
            if (imageVisibility.captureId == null) {
                throw new Error('Unable to read image visibility, missing captureId ' + imageVisibilityId);
            }
            const imageVisibilityState = this.collection.getImageVisibilityState(imageVisibility.captureId);
            if (imageVisibilityState.imageVisibility) {
                continue;
            }
            if (ReadUtil.isHiddenImageVisibility(imageVisibility.state)) {
                this.createHiddenImageVisibility(imageVisibilityState, imageVisibility.state);
            }
        }
    }
    createGammaBrightnessCorrection(brightnessCorrectionState, obj) {
        const gammaId = brightnessCorrectionState.Mutator.create('gamma', obj.id);
        const gamma = brightnessCorrectionState.getBrightnessCorrection(gammaId);
        const gammaState = gamma.state;
        gammaState.exponent = obj.exponent;
        return gamma;
    }
    createHiddenImageVisibility(hiddenImageVisibilityState, obj) {
        const hidden = hiddenImageVisibilityState.getImageVisibility();
        const hiddenState = hidden.state;
        hiddenState.hidden = obj.hidden;
        return hidden;
    }
    readCropParameters() {
        const collectionJSON = this.json;
        if (collectionJSON.cropParameters == null) {
            return;
        }
        const cropParameters = Object.keys(collectionJSON.cropParameters);
        if (cropParameters.length === 0) {
            return;
        }
        for (const cropParameterId of cropParameters) {
            const cropParameter = collectionJSON.cropParameters[cropParameterId];
            if (cropParameter == null) {
                continue;
            }
            if (cropParameter.state.length === 0) {
                continue;
            }
            if (cropParameter.captureId == null) {
                throw new Error('Unable to read crop parameter, missing captureId ' + cropParameterId);
            }
            const cropParameterState = this.collection.getCropParameterState(cropParameter.captureId);
            for (const drawableCropParameter of cropParameter.state) {
                if (cropParameterState.cropParameters[drawableCropParameter.id]) {
                    continue;
                }
                if (ReadUtil.isFreeCropParameter(drawableCropParameter)) {
                    this.createFreeCropParameter(cropParameterState, drawableCropParameter);
                }
                else {
                    continue; // TODO should we error here?
                }
            }
        }
    }
    createFreeCropParameter(cropParameterState, obj) {
        const freeId = cropParameterState.Mutator.create('free', obj.id);
        const free = cropParameterState.getCropParameter(freeId);
        const freeState = free.state;
        freeState.x = obj.x;
        freeState.y = obj.y;
        freeState.width = obj.width;
        freeState.height = obj.height;
        return free;
    }
}
exports.IkeModelCollectionReader = IkeModelCollectionReader;
//# sourceMappingURL=collection.model.read.js.map